import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface DedupedPackage {
  id?: string;
  name: string;
  tagline?: string;
  priceLabel: string;
  features: string[];
  icon?: "check" | "plus" | "zap";
  highlight?: boolean;
  highlightLabel?: string;
  ctaLabel?: string;
  ctaTarget?: string;
  comingSoon?: boolean;
}

interface ServicesTiersDedupedProps {
  headline?: string;
  subheadline?: string;
  packages?: DedupedPackage[];
}

const DEFAULT_PACKAGES: DedupedPackage[] = [
  {
    name: "Quick Detail",
    tagline: "Essential Maintenance",
    priceLabel: "$89",
    icon: "check",
    features: [
      "Hand wash & microfiber dry",
      "Wheel, tire & arch cleaning",
      "Streak-free glass treatment",
      "Long-lasting tire dressing",
    ],
    ctaLabel: "Book Now",
  },
  {
    name: "Full Detail",
    tagline: "Total Rejuvenation",
    priceLabel: "$189",
    icon: "plus",
    highlight: true,
    highlightLabel: "Most Popular",
    features: [
      "Full exterior decontamination",
      "Deep cabin vacuum & air purge",
      "Dash, console & trim dressing",
      "Anti-microbial carpet cleaning",
      "Odor neutralization treatment",
    ],
    ctaLabel: "Book Now",
  },
  {
    name: "Premium Detail",
    tagline: "Elite Protection",
    priceLabel: "from $399",
    icon: "zap",
    features: [
      "Paint correction & clay bar",
      "Industrial bug & tar removal",
      "High-gloss ceramic sealant",
      "Hot water extraction shampoo",
      "Headlight lens restoration",
    ],
    ctaLabel: "Request Quote",
  },
];

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${className ?? "text-primary"}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M22 4 12 14.01l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${className ?? "text-primary"}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ZapIcon({ className }: { className?: string }) {
  return (
    <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${className ?? "text-primary"}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TierIcon({ type, className }: { type: DedupedPackage["icon"]; className?: string }) {
  if (type === "plus") return <PlusIcon className={className} />;
  if (type === "zap") return <ZapIcon className={className} />;
  return <CheckIcon className={className} />;
}

export function ServicesTiersDeduped({ props }: SectionComponentProps) {
  const {
    headline = "Service Tiers",
    subheadline = "Precision packages tailored to your vehicle's condition. Each tier lists only what it adds — no fine print.",
    packages = DEFAULT_PACKAGES,
  } = props as ServicesTiersDedupedProps;

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(".std-headline-word",
        { yPercent: 110, opacity: 0 },
        { yPercent: 0, opacity: 1, duration: 0.75, ease: "power3.out", stagger: 0.07,
          scrollTrigger: { trigger: ".std-header", start: "top 88%", toggleActions: "play none none none" } }
      );
      gsap.fromTo(".std-subheadline", { opacity: 0, y: 16 }, {
        opacity: 1, y: 0, duration: 0.6, ease: "power3.out",
        scrollTrigger: { trigger: ".std-header", start: "top 82%", toggleActions: "play none none none" },
      });

      const cards = el.querySelectorAll<HTMLElement>(".std-card");
      gsap.fromTo(cards,
        { opacity: 0, y: 80, scale: 0.92, rotateX: 10 },
        { opacity: 1, y: 0, scale: 1, rotateX: 0, duration: 0.9, ease: "power3.out", stagger: 0.14,
          transformOrigin: "top center",
          scrollTrigger: { trigger: ".std-grid", start: "top 85%", toggleActions: "play none none none" } }
      );

      cards.forEach((card) => {
        const items = card.querySelectorAll(".std-feature-item");
        gsap.fromTo(items, { opacity: 0, x: -16 }, {
          opacity: 1, x: 0, duration: 0.45, ease: "power2.out", stagger: 0.07,
          scrollTrigger: { trigger: card, start: "top 78%", toggleActions: "play none none none" },
        });
      });

      const highlighted = el.querySelector<HTMLElement>(".std-card-highlight");
      if (highlighted) {
        ScrollTrigger.create({
          trigger: highlighted,
          start: "top 75%",
          onEnter: () => {
            gsap.fromTo(highlighted,
              { boxShadow: "0 0 0px 0px rgba(224,92,26,0)" },
              { boxShadow: "0 0 48px 8px rgba(224,92,26,0.22)", duration: 1.2, ease: "power2.out", delay: 0.6 }
            );
          },
        });
      }
    }, el);

    return () => ctx.revert();
  }, []);

  const headlineWords = headline.split(" ");

  return (
    <section
      ref={sectionRef}
      className="py-28 px-6 bg-background border-t border-border"
      id="services"
    >
      <div className="max-w-6xl mx-auto">
        {(headline || subheadline) && (
          <div className="std-header text-center mb-20">
            {headline && (
              <h2 className="text-5xl md:text-7xl font-bold text-foreground mb-5 tracking-tight font-display uppercase">
                {headlineWords.map((w, i) => (
                  <React.Fragment key={i}>
                    <span className="inline-block overflow-hidden leading-[1.15] align-bottom">
                      <span className="std-headline-word inline-block">{w}</span>
                    </span>
                    {i < headlineWords.length - 1 && " "}
                  </React.Fragment>
                ))}
              </h2>
            )}
            {subheadline && (
              <p className="std-subheadline text-muted-foreground max-w-xl mx-auto text-base leading-relaxed">
                {subheadline}
              </p>
            )}
          </div>
        )}

        <div
          className={[
            "std-grid grid gap-8",
            packages.length === 2 ? "md:grid-cols-2 max-w-2xl mx-auto" :
            packages.length === 3 ? "md:grid-cols-3 items-start" :
            "md:grid-cols-2 lg:grid-cols-4",
          ].join(" ")}
          style={{ perspective: "1200px" }}
        >
          {packages.map((pkg, i) => (
            <div
              key={pkg.id ?? pkg.name ?? i}
              data-stagger-child
              className={[
                "std-card relative flex flex-col overflow-hidden border transition-all duration-300",
                pkg.highlight
                  ? "std-card-highlight border-primary scale-105 z-10 shadow-2xl shadow-primary/20"
                  : "border-border hover:border-primary/40 hover:shadow-xl hover:shadow-black/10",
              ].join(" ")}
              style={{ borderRadius: "var(--radius, 2px)" }}
            >
              {/* Card header — dark industrial */}
              <div
                className={[
                  "relative overflow-hidden px-8 pt-8 pb-7",
                  pkg.highlight ? "bg-primary" : "bg-foreground",
                ].join(" ")}
              >
{pkg.highlight && pkg.highlightLabel && (
                  <span
                    className="absolute top-3 right-3 bg-black/20 text-white text-[9px] font-bold uppercase tracking-[0.22em] px-3 py-1"
                    style={{ borderRadius: "var(--radius, 2px)" }}
                  >
                    {pkg.highlightLabel}
                  </span>
                )}

                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-3">
                    <TierIcon
                      type={pkg.icon}
                      className={pkg.highlight ? "text-white/80" : "text-primary"}
                    />
                    {pkg.tagline && (
                      <span className={[
                        "text-[10px] font-bold uppercase tracking-[0.2em]",
                        pkg.highlight ? "text-white/70" : "text-primary",
                      ].join(" ")}>
                        {pkg.tagline}
                      </span>
                    )}
                  </div>

                  <h3 className="font-bold text-xl text-background font-display uppercase tracking-tight mb-4">
                    {pkg.name}
                  </h3>

                  <div className="text-4xl font-black text-background font-display tabular-nums">
                    {pkg.priceLabel}
                  </div>
                </div>
              </div>

              {/* Card body */}
              <div className="flex flex-col flex-1 bg-card px-8 py-7">
                {pkg.features && pkg.features.length > 0 && (
                  <ul className="space-y-3.5 mb-8 flex-1">
                    {pkg.features.map((feat, fi) => (
                      <li key={fi} className="std-feature-item flex items-start gap-3 text-sm text-muted-foreground">
                        <TierIcon type={pkg.icon} />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {pkg.comingSoon ? (
                  <div
                    className="w-full text-center py-4 text-[11px] font-bold uppercase tracking-[0.22em] font-display bg-muted/40 text-muted-foreground border border-border/60 cursor-default select-none"
                    style={{ borderRadius: "var(--radius, 2px)" }}
                    aria-disabled="true"
                  >
                    Coming Soon
                  </div>
                ) : (
                  <a
                    href={pkg.ctaTarget ?? "#services"}
                    className={[
                      "block w-full text-center py-4 text-sm font-bold uppercase tracking-widest transition-colors duration-200 font-display",
                      pkg.highlight
                        ? "bg-primary text-primary-foreground hover:opacity-90"
                        : "border border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary",
                    ].join(" ")}
                    style={{ borderRadius: "var(--radius, 2px)" }}
                  >
                    {pkg.ctaLabel ?? "Book Now"}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
